import { Icon } from '@iconify/react'
import { observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { Step, Stepper } from '@mui/material'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLoaderData } from '@tanstack/react-router'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { Check } from 'lucide-react'
import React, { Fragment, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ulid } from 'ulidx'

import { fetchLicenseContents, fetchLicenseDetails, updateLicense } from '~features/licenses/api'
import { LicenseProductFeatureSetting, Licenses, LicenseSites } from '~features/licenses/types'
import { fetchProductFeatureList } from '~features/product-features/api'
import { fetchProductPrivilegeList } from '~features/product-privileges/api'
import { fetchHostAdminTemplateList, fetchTemplateList } from '~features/templates/api'
import { Templates } from '~features/templates/types'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  // CardFooter,
  IconButton,
  Input,
  Label,
  Textarea,
} from '~in-app/components'
import DatePicker from '~in-app/components/Datepicker'
import Dropdown from '~in-app/components/Dropdown'
import MultiSelectDropdown, {
  Option,
} from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchDistributorDivisionList, fetchDistributorList, fetchDropDownIntegratorList, fetchProductByIds } from '~in-app/shareable/index.api'
import { Product } from '~in-app/types'
import { licenseValidation } from '~in-app/validation/licenses.validation'

export const Route = createFileRoute('/console/sites/licenses/edit/$id')({
  component: EditLicensePage,
  loader: async ({ params: { id }, abortController }) => {
    const privileges = auth$.privileges.get()
    let data = {} as Licenses
    if (privileges?.has('0.3.1.9')) {
      const response = await fetchLicenseDetails({ id })
      data = response?.[0] ?? {} as Licenses
    }
    let reponseFeatureList = [] as any[]
    // let productFeatureSettingsResponse = [] as any[]
    // if (privileges?.has('0.3.1.9')) {
    //   const response = await fetchProductFeatureList()
    //   // featureList = (response?.list ?? [])
    //   //   .filter((val: any) => val.priviledges !== undefined)
    //   //   .map((item: any) => ({
    //   //     featureValue: item?.priviledges[0]?.id,
    //   //     featureName: item.name,
    //   //     featureCode: item.code,
    //   //     featureId: item.id,
    //   //     privileges: item.priviledges,
    //   //     label: item.name,
    //   //     value: item.id,
    //   //   }))

    //   productFeatureSettingsResponse = (response?.list ?? [])
    //     .filter((val: any) => val.priviledges === undefined)
    //     .map((item: any) => ({
    //       featureValue: item.defaultValue,
    //       featureName: item.name,
    //       featureCode: item.code,
    //       featureId: item.id,
    //       privileges: item.priviledges,
    //     }))
    // }
    return { data }
  },
  staticData: {
    privileges: ['0.3.1.9'],
  },
})

function EditLicensePage() {
  const { data } = useLoaderData({ from: Route.id })
  const router = useRouter()
  const state$ = useObservable({
    productIds: '',
    data: {} as Licenses,
    featureList: [] as Option[],
  })
  const state$2 = useObservable({
    selectedProductTemplate: {} as Templates,
    productTemplateList: [] as Templates[],
    unfilteredFeatureList: [] as any[],
  })
  const [activeStep, setActiveStep] = useState(0)
  const [collapsedIds, setCollapsedIds] = useState<string[]>([])

  const steps = ['License Details', 'Product Feature Settings']
  const formOpts = formOptions({
    defaultValues: {
      activationExpiryTime: new Date(
        new Date().setDate(new Date().getDate() + 7),
      ),
      licenseExpiryDate: new Date(
        new Date().setFullYear(
          new Date().getFullYear() + 99,
          new Date().getMonth(),
          new Date().getDate() + 3,
        ),
      ),
      product: [] as Product[],
      productId: '',
      remarks: '',
      version: 0,
      certNum: '',
      current: false,
      privilegeFeatures: [] as any[],
      productFeatureSettings: [] as any[],
      productTemplateId: '',
      siteId: '',
      site: {} as LicenseSites,
      clientId: '',
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: licenseValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks
      try {
        submitLicense(value)
      }
      catch (error) {
        console.error('Add Integrators failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })
  const formControl = useForm(formOpts)
  const queryClient = useQueryClient()
  const fetchFeatureList = async () => {
    const response = await fetchProductFeatureList({ query: { productIds: state$.productIds.get() } })
    const reponseFeatureList = state$2.selectedProductTemplate.get()?.featureSettings
      ? state$2.selectedProductTemplate.get()?.featureSettings.filter((val: any) => val.privileges !== undefined)
        .map((item: any) => ({
          featureValue: item?.featureValue,
          featureName: item.featureName,
          featureCode: item.featureCode,
          type: item.type,
          featureId: item.featureId,
          privileges: item.privileges,
          label: item.featureName,
          value: item.featureId,
        }))
      : (response?.list ?? [])
          .filter((val: any) => val.priviledges !== undefined)
          .map((item: any) => ({
            featureValue: item?.priviledges[0]?.id,
            featureName: item.name,
            featureCode: item.code,
            type: item.type,
            featureId: item.id,
            privileges: item.priviledges,
            label: item.name,
            value: item.id,
          }))

    const productFeatureSettingsResponse = state$2.selectedProductTemplate.get()?.featureSettings
      ? state$2.selectedProductTemplate.get()?.featureSettings.filter((val: any) => val.priviledges === undefined).map((item: any) => ({
        featureValue: item.featureValue,
        type: item.type,
        featureName: item.featureName,
        featureCode: item.featureCode,
        featureId: item.featureId,
        privileges: item.privileges,
      }))
      : (response?.list ?? [])
          .filter((val: any) => val.priviledges === undefined)
          .map((item: any) => ({
            featureValue: item.defaultValue,
            type: item.type,
            featureName: item.name,
            featureCode: item.code,
            featureId: item.id,
            privileges: item.priviledges,
          }))
    // Set the form value of productFeatureSettings
    formControl.setFieldValue('productFeatureSettings', productFeatureSettingsResponse)
    state$.featureList.set(reponseFeatureList)
    const unfilteredFeatureList = (response?.list ?? [])
      .filter((val: any) => val.priviledges !== undefined)
      .map((item: any) => ({
        featureValue: item?.priviledges[0]?.id,
        type: item.type,
        featureName: item.name,
        featureCode: item.code,
        featureId: item.id,
        privileges: item.priviledges,
        label: item.name,
        value: item.id,
      }))
    state$2.unfilteredFeatureList.set(unfilteredFeatureList)
    return reponseFeatureList
  }

  const updateFormWithData = () => {
    if (data) {
      Object.entries(data).forEach(([key, val]) => {
        if (key !== 'productFeatureSettings') {
          formControl.setFieldValue(key as any, val ?? '')
        }
      })
      const updatedProductFeatureSettings: LicenseProductFeatureSetting[] = data?.productFeatureSettings
        .filter(val => val.privileges === undefined)
        .map((setting) => {
          const matchedItem = data?.productFeatureSettings.find(
            (val: any) => val.featureName === setting.featureName,
          )
          if (matchedItem) {
            return {
              ...setting,
              featureValue: matchedItem.featureValue,
              defaultValue: matchedItem.featureValue,
            }
          }
          return setting
        })
      // Compare fields and add any missing ones from productFeatureSettingsResponse
      const productFeatureSettingsResponseList = formControl.getFieldValue('productFeatureSettings')
      const completeProductFeatureSettings = productFeatureSettingsResponseList?.map((setting) => {
        const matchingResponseSetting = updatedProductFeatureSettings.find(
          resp => resp.featureId === setting.featureId,
        )
        if (matchingResponseSetting) {
          return {
            ...setting,
            defaultValue: setting.defaultValue, // Keep the updated default value
            ...matchingResponseSetting,
          }
        }
        return setting
      })
      formControl.setFieldValue(
        'productFeatureSettings',
        completeProductFeatureSettings,
      )
      formControl.setFieldValue('siteId', data?.site?.id)
      formControl.setFieldValue('productId', data?.product?.id)
      formControl.setFieldValue('clientId', data?.site?.clientId ?? auth$.clientId.get() ?? '')

      formControl.setFieldValue('site', data?.site)
      state$.productIds.set(data?.product?.id)

      formControl.setFieldValue(
        'activationExpiryTime',
        new Date(data.activationExpiryTime),
      )
      formControl.setFieldValue(
        'licenseExpiryDate',
        new Date(data.licenseExpiryDate),
      )
      state$.data.set(data)
      queryClient.invalidateQueries({ queryKey: ['edit-licenses-product-template-listing'] })
      formControl.setFieldValue('productTemplateId', data?.productTemplate?.id ?? '')
    }
  }

  useEffect(() => {
    if (data) {
      state$.productIds.set(data?.product?.id)
      fetchFeatureList().then(updateFormWithData)
    }
  }, [data])
  // const { data: data = {} as Licenses, isSuccess: isSuccessDetails } = useQuery<Licenses>({ // Add explicit type here
  //   queryKey: ['edit-licenses-details-listing'],
  //   queryFn: async () => {
  //     if (!auth$.privileges.get()?.has('0.3.1.5')) {
  //       return {} as Licenses
  //     }

  //     const response = await fetchLicenseDetails({
  //       id: id,
  //     })
  //     return response?.[0] ?? {} as Licenses
  //   },
  // })
  const { data: divisionList = [], isSuccess: isSuccessDivision } = useQuery<Option[]>({
    queryKey: ['edit-licenses-division-listing'],
    queryFn: async ({ signal }) => {
      const clientId = auth$.clientId.get() ?? (data?.site?.clientId ?? '')
      if (!clientId || !auth$.privileges.get()?.has('0.4.0.0')) {
        return [] as Option[]
      }

      const response = await fetchDistributorDivisionList({
        clientId: clientId,
      })

      const flattenDivisionsWithReduce = (
        divisions: any[],
        parentId: string | null = null,
      ): any[] => {
        return divisions.reduce((acc: any[], division: any) => {
          acc.push({
            value: division.id,
            label: division.name,
            parentId: parentId,
          })

          if (division.mychilds && division.mychilds.length > 0) {
            acc = acc.concat(
              flattenDivisionsWithReduce(division.mychilds, division.id),
            )
          }

          return acc
        }, [])
      }

      const flatDivisionList = flattenDivisionsWithReduce(response.userDivisions)
      return flatDivisionList
    },
  })

  const useDownloadLicense = () => {
    const mutation = useMutation({
      mutationFn: fetchLicenseContents,
      onSuccess: () => {
        toast.success('License downloaded successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const downloadMutation = useDownloadLicense()

  const handleDownloadLicense = (id: string, certNum: string) => {
    downloadMutation.mutate({ id, certNum })
  }

  const { data: productList = [] } = useQuery<Option[]>({
    queryKey: ['add-licenses-products-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const productIds = data?.site?.products?.map((item: any) => item.id) || []
      if (productIds.length === 0) {
        return [] as Option[] // Return empty if productIds is empty
      }

      const response = await fetchProductByIds(productIds, {
        abortCtrl: signal,
      })
      const productList = (response ?? []).map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      return productList
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const { data: productTemplateList = [] } = useQuery<Option[]>({
    queryKey: ['edit-licenses-product-template-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (state$.productIds.get().length === 0) {
        return [] as Option[] // Return empty if productIds is empty
      }

      const fetchAllTemplates = async (page = 0, accumulatedTemplates: any[] = []): Promise<any[]> => {
        if (!auth$.clientId.get() && !formControl.getFieldValue('clientId')) {
          return accumulatedTemplates // Return empty if no clientId
        }

        const response = auth$.clientId.get()
          ? await fetchTemplateList({ abortCtrl: signal, query: { page } })
          : await fetchHostAdminTemplateList({ abortCtrl: signal, query: { clientId: formControl.getFieldValue('clientId') ?? '', page } })

        const newTemplates = accumulatedTemplates.concat(response?.list ?? [])
        if (response?.totalPage && page + 1 < response.totalPage) {
          return fetchAllTemplates(page + 1, newTemplates)
        }
        return newTemplates
      }

      const allTemplates = await fetchAllTemplates()
      const productTemplateList = allTemplates
        .filter((item: any) => state$.productIds.get().includes(item.product.id))
        .map((item: any) => ({
          label: item.name,
          value: item.id,
        })) || [] as Option[]
      state$2.productTemplateList.set(allTemplates)
      return productTemplateList
    },
  })

  const { data: distributorList = [] } = useQuery<Option[]>({
    queryKey: ['edit-licenses-distributor-listing'],
    queryFn: async () => {
      if (!auth$.privileges.get()?.has('0.3.7.0')) {
        return [] as Option[]
      }
      const response = await fetchDistributorList()
      const distributorList = response?.list.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) || [] as Option[]

      return distributorList
    },
  })

  const { data: integratorList = [] } = useQuery<Option[]>({
    queryKey: ['edit-licenses-integrator-listing'],
    queryFn: async () => {
      const clientId = auth$.clientId.get() ?? (data?.site?.clientId ?? '')
      if (!clientId || !auth$.privileges.get()?.has('0.3.0.0')) {
        return [] as Option[]
      }

      const response = await fetchDropDownIntegratorList({
        clientIds: clientId,
      })
      const integratorList = response.list.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))

      return integratorList
    },
  })
  function buildPrivilegeTree(privileges: any[]) {
    const privilegeMap = new Map()

    // Step 1: Initialize all privileges in a map
    privileges.forEach((privilege) => {
      if (privilege.value.length === 3) {
        setCollapsedIds(prev => [...prev, privilege.id])
      }

      const isSpecialValue = ['0', '0.1', '0.3', '0.3.7', '0.3.7.0', '0.3.7.1'].includes(privilege.value)
      privilegeMap.set(privilege.id, {
        ...privilege,
        children: [],
        isExpanded: true,
        isChecked: true,
        isDisable: isSpecialValue,
      })
    })

    // Step 2: Link children to their parents
    privileges.forEach((privilege) => {
      if (privilege.parent) {
        const parentPrivilege = privilegeMap.get(privilege.parent)
        if (parentPrivilege) {
          parentPrivilege.children.push(privilegeMap.get(privilege.id))
          // Sort children by value in ascending order
          parentPrivilege.children.sort((a: { value: string }, b: { value: any }) => a.value.localeCompare(b.value, undefined, { numeric: true }))
        }
      }
    })
    // selectedFeatureValues.includes(privilege.value)
    const filteredTreeData = Array.from(privilegeMap.values()).filter((privilege: any) => !privilege.parent)
    // Step 3: Return the root-level privileges
    const newFeatureList = state$2.unfilteredFeatureList.get()

    return filteredTreeData.map((privilege: any) => {
      const matchedFeature = newFeatureList?.find((feature: any) => feature.featureValue === privilege.value)
      if (matchedFeature) {
        return {
          ...matchedFeature,
          isChecked: true,
          fixed: privilege.value === '0' ? true : undefined,
          isDisable: true,
          parent: privilege.parent,
          children: privilege.children,
          isExpanded: privilege.isExpanded,
        }
      }
      return privilege
    })
  }
  const { data: productPrivilegeList = [], refetch } = useQuery<Option[]>({
    queryKey: ['edit-licenses-product-privileges-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (state$.productIds.get() === '') {
        return [] as Option[]
      }
      const fetchAllPrivileges = async (page = 0, accumulatedPrivileges: any[] = []): Promise<any[]> => {
        const response = await fetchProductPrivilegeList({ query: { productIds: state$.productIds.get(), page, size: 100 } })
        if (response) {
          const newPrivileges = accumulatedPrivileges.concat(response.list)
          if (page + 1 < response.totalPage) {
            return fetchAllPrivileges(page + 1, newPrivileges)
          }
          return newPrivileges
        }
        return accumulatedPrivileges
      }

      const allPrivileges = await fetchAllPrivileges()
      const privilegeList = allPrivileges.map((item: any) => ({
        label: item.name,
        value: item.code,
        parent: item?.parent?.id,
        id: item.id,
        featureValue: item.featureValue,
        type: item.type,
        featureName: item.featureName,
        featureCode: item.featureCode,
        featureId: item.featureId,
      }))
      const productResponse = buildPrivilegeTree(privilegeList)
      const updateIsChecked = (features: any[]): any[] => {
        return features.map((feature: any) => {
          const responseList = state$2.selectedProductTemplate.get()?.featureSettings ? state$2.selectedProductTemplate.get()?.featureSettings : state$.data.get()?.productFeatureSettings
          const matchedSetting = responseList.filter((setting: any) => setting.privileges !== undefined).find((setting: any) => setting.featureValue === feature.featureValue)
          if (matchedSetting) {
            const updateChildren = (children: any[], privileges: any[]): any[] => {
              return children.map((child: any) => {
                const matchedPrivilege = state$2.selectedProductTemplate.get()?.featureSettings ? privileges.find((privilege: any) => privilege === child.value) : privileges.find((privilege: any) => privilege.id === child.value)
                if (matchedPrivilege) {
                  return {
                    ...child,
                    isChecked: true,
                    children: child.children ? updateChildren(child.children, privileges) : [],
                  }
                }
                return {
                  ...child,
                  isChecked: false,
                  children: child.children ? updateChildren(child.children, privileges) : [],
                }
              })
            }
            return {
              ...feature,
              isChecked: true,
              children: feature.children ? updateChildren(feature.children, matchedSetting.privileges || []) : [],
            }
          }
          return {
            ...feature,
            isChecked: true,
            children: feature.children ? updateIsChecked(feature.children) : [],
          }
        })
      }
      const productTemplateId = formControl.getFieldValue('productTemplateId')
      if (state$2.selectedProductTemplate.get()?.featureSettings) {
        const updatedCheckedList = updateIsChecked(productResponse)
        const selectedTemplate = state$2.selectedProductTemplate.get()?.featureSettings.filter((setting: any) => setting.privileges !== undefined)
        formControl.setFieldValue('privilegeFeatures', updatedCheckedList.filter((feature: any) => selectedTemplate.map((setting: any) => setting.featureValue).includes(feature.featureValue)))
      }
      else if ((data?.productTemplate?.id || '') === productTemplateId) {
        const updatedCheckedList = updateIsChecked(productResponse)
        const finalValue = updatedCheckedList.filter(feature =>
          state$.data.get()?.productFeatureSettings
            .filter((setting: any) => setting.privileges !== undefined)
            .some((setting: any) => setting.featureValue === feature.featureValue),
        )
        formControl.setFieldValue('privilegeFeatures', finalValue)
      }
      else {
        const centralFeature = productResponse.find((feature: any) => feature.featureValue === '0' && feature.featureCode === 'MODULE_CENTRAL')
        formControl.setFieldValue('privilegeFeatures', centralFeature ? [{ ...centralFeature }] : [])
      }
      return productResponse.filter((feature: any) => feature.featureValue && feature.featureName && feature.featureCode && feature.featureId)
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  useEffect(() => {
    const disposer = observe(state$2.unfilteredFeatureList, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])
  const updateLicenseFunc = () => {
    const mutation = useMutation({
      mutationFn: updateLicense,
      onSuccess: (response) => {
        toast.success(`License Updated Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/sites/licenses/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const updateLicenseMutation = updateLicenseFunc()
  const submitLicense = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      const collectCheckedPrivileges = (privileges: any[]): string[] => {
        let checkedPrivileges: string[] = []
        privileges.forEach((privilege: any) => {
          if (privilege.isChecked) {
            if (privilege.featureValue) {
              checkedPrivileges.push(privilege.featureValue)
            }
            else {
              checkedPrivileges.push(privilege.value)
            }
          }
          if (privilege.children) {
            checkedPrivileges = checkedPrivileges.concat(collectCheckedPrivileges(privilege.children))
          }
        })
        return checkedPrivileges
      }
      submitValue.productFeatureSettings = submitValue.productFeatureSettings
        .filter((item: any) => item.featureValue !== '')
        .concat(
          ...submitValue.privilegeFeatures.map((item: any) => ({
            featureId: item.featureId,
            featureValue: item.featureValue,
            featureName: item.featureName,
            privileges: collectCheckedPrivileges([item]),
          })).sort((a: any, b: any) => a.featureValue - b.featureValue),
        )
      delete submitValue.privilegeFeatures
      Object.entries(submitValue).forEach(([key, value]) => {
        if (value === '') {
          delete submitValue[key]
        }
      })
      updateLicenseMutation.mutate(submitValue)
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  const handleNext = async (e: React.MouseEvent) => {
    e.preventDefault() // Prevent form submission
    if (activeStep === steps.length - 1) {
      formControl.handleSubmit()
    }
    else {
      // Validate current step before proceeding
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )
        const isValid = validationResults.every(({ result }) => Array.isArray(result) && result.length === 0)
        if (isValid) {
          setActiveStep(prevActiveStep => prevActiveStep + 1)
        }
        else {
          for (const { field, result: errors } of validationResults) {
            if (errors.length > 0) {
              const fieldElement = document.getElementById(field)
              fieldElement?.focus()
              // Scroll to the field element if it exists
              fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              break
            }
          }
        }
      }
    }
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="p-2">
          <IconButton
            color="muted"
            onClick={() => router.history.back()}
          >
            <Icon icon="lucide:arrow-left" />
          </IconButton>

          <span className="mb-4 ml-4 text-2xl font-bold">
            View License Details
          </span>
        </div>

        <div>
          <Button
            className="rounded-full border-none bg-transparent p-2 text-current hover:opacity-100"
            color="muted"
            variant="outlined"
            onClick={() => handleDownloadLicense(data?.id, data?.certNum)}
          >
            Download
          </Button>
        </div>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Stepper
                  activeStep={activeStep}
                  style={{ width: '70%' }}
                >
                  {
                    steps.map((label, index) => (
                      <Step key={label}>
                        <div className="flex flex-col items-center justify-center space-y-2">
                          {/* Step Circle */}
                          <div
                            className={
                              `flex size-8 items-center justify-center rounded-full border-2 ${
                                index < activeStep
                                  ? 'border-blue-500 bg-blue-500 text-white' // Completed step
                                  : index === activeStep
                                    ? 'border-blue-500 bg-blue-500 text-white' // Active step
                                    : 'border-gray-300 bg-gray-200 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400' // Inactive step
                              }`
                            }
                          >
                            {/* Render Checkmark for completed steps */}
                            {
                              index < activeStep
                                ? (
                                    <Check className="text-white" /> // Check icon for completed steps
                                  )
                                : (
                                    index + 1 // Step number for active or future steps
                                  )
                            }
                          </div>

                          {/* Custom Label */}
                          <div
                            className={
                              `text-sm ${
                                index <= activeStep
                                  ? 'text-gray-900 dark:text-gray-100' // Active/completed label
                                  : 'text-gray-500 dark:text-gray-400' // Inactive label
                              }`
                            }
                          >
                            {label}
                          </div>
                        </div>
                      </Step>
                    ))
                  }

                </Stepper>

              </div>

              {
                activeStep === 0 && (

                  <>
                    <h4 className="text-xl font-semibold">License Details</h4>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid">
                        <formControl.Field name="certNum">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="licenseCertNumber"
                                  >
                                    Cert No.
                                  </Label>

                                  <div className="relative flex w-full flex-row items-center self-center">
                                    <Input
                                      id="certNum"
                                      type="text"
                                      value={field.state.value}
                                      onBlur={field.handleBlur}
                                      onChange={
                                        (e) => {
                                          field.handleChange(e.target.value),
                                          field.validate('change')
                                        }
                                      }
                                    />

                                    <span className="ml-2">
                                      <IconButton
                                        className="rounded-full"
                                        color="muted"
                                        type="button"
                                        variant="text"
                                        onClick={
                                          () => {
                                            const id = ulid()
                                            field.handleChange(id)
                                          }
                                        }
                                      >
                                        <Icon icon="lucide:refresh-cw" />
                                      </IconButton>
                                    </span>
                                  </div>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <div className="relative">
                          <Label
                            className="text-xs"
                            htmlFor="currentStatus"
                          >
                            Status
                          </Label>

                          <Input
                            disabled
                            id="currentStatus"
                            type="text"
                            value={
                              data?.current ? 'Active' : 'Inactive'
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="mb-2 grid">
                        <formControl.Field name="activationExpiryTime">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="licenseActivationExpiryTime"
                                  >
                                    Activation Expiry Time
                                  </Label>

                                  <DatePicker
                                    className="block w-full"
                                    disabled="before"
                                    id="licenseActivationExpiryTime"
                                    selectedDate={field.state.value} // Ensure a valid start date
                                    disableStartDate={
                                      formControl.getFieldValue(
                                        'activationExpiryTime',
                                      ) || new Date()
                                    }
                                    onDateChange={
                                      (e) => {
                                        field.handleChange(e),
                                        field.validate('change')
                                        const date = new Date(e)
                                        date.setFullYear(date.getFullYear() + 1)
                                        formControl.setFieldValue(
                                          'licenseExpiryDate',
                                          date,
                                        )
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="mb-2 grid">
                        <formControl.Field name="licenseExpiryDate">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="licenseLicenseExpiryDate"
                                  >
                                    License Expiry Date
                                  </Label>

                                  <DatePicker
                                    className="block w-full"
                                    disabled="before"
                                    id="licenseLicenseExpiryDate"
                                    selectedDate={field.state.value} // Ensure a valid start date
                                    onDateChange={
                                      (e) => {
                                        field.handleChange(e),
                                        field.validate('change')
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-1">
                      {/* Description Field */}

                      {/* Remarks Field */}
                      <div className="grid">
                        <formControl.Field name="remarks">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="remarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-2">
                        <formControl.Field name="productId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="siteServerType"
                                  >
                                    Product
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          id="productId"
                                          label=""
                                          options={productList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                                state$.productIds.set(e)
                                                queryClient.invalidateQueries({ queryKey: ['edit-licenses-product-template-listing'] })
                                                queryClient.invalidateQueries({ queryKey: ['edit-licenses-product-privileges-listing'] })
                                                fetchFeatureList()
                                                state$2.selectedProductTemplate.set({} as Templates)
                                                formControl.setFieldValue('productTemplateId', '')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field name="productTemplateId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="productTemplateId"
                                  >
                                    Product Template (Optional)
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          isClearable
                                          id="productTemplateId"
                                          label=""
                                          options={productTemplateList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                                const selectedProductTemplate = state$2.productTemplateList.get().find((val: any) => val.id === e) ?? {} as Templates
                                                state$2.selectedProductTemplate.set(selectedProductTemplate)
                                                if (data?.productTemplate?.id === e) {
                                                  const updatedProductFeatureSettings: LicenseProductFeatureSetting[] = data?.productFeatureSettings
                                                    .filter(val => val.privileges === undefined)
                                                    .map((setting) => {
                                                      const matchedItem = data?.productFeatureSettings.find(
                                                        (val: any) => val.featureName === setting.featureName,
                                                      )
                                                      if (matchedItem) {
                                                        return {
                                                          ...setting,
                                                          featureValue: matchedItem.featureValue,
                                                          defaultValue: matchedItem.featureValue,
                                                        }
                                                      }
                                                      return setting
                                                    })
                                                  // Compare fields and add any missing ones from productFeatureSettingsResponse
                                                  const productFeatureSettingsResponseList = formControl.getFieldValue('productFeatureSettings')
                                                  const completeProductFeatureSettings = productFeatureSettingsResponseList?.map((setting) => {
                                                    const matchingResponseSetting = updatedProductFeatureSettings.find(
                                                      resp => resp.featureId === setting.featureId,
                                                    )
                                                    if (matchingResponseSetting) {
                                                      return {
                                                        ...setting,
                                                        defaultValue: setting.defaultValue, // Keep the updated default value
                                                        ...matchingResponseSetting,
                                                      }
                                                    }
                                                    return setting
                                                  })
                                                  formControl.setFieldValue(
                                                    'productFeatureSettings',
                                                    completeProductFeatureSettings,
                                                  )
                                                }
                                                else {
                                                  fetchFeatureList()
                                                }
                                                // queryClient.invalidateQueries({ queryKey: ['edit-licenses-features-listing'] })
                                                queryClient.invalidateQueries({ queryKey: ['edit-licenses-product-privileges-listing'] })
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <h4 className="text-lg font-semibold">Site Details</h4>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-2">
                        <formControl.Field name="site.name">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="siteName"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    disabled
                                    className="mt-1"
                                    id="siteName"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field name="site.systemId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="siteSystemId"
                                  >
                                    System ID
                                  </Label>

                                  <Input
                                    disabled
                                    className="mt-1"
                                    id="siteSystemId"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className={`grid ${!auth$.privileges.get()?.has('0.3.7.0') && auth$.clientId.get() ? 'grid-cols-2 gap-2' : 'grid-cols-3 gap-3'}`}>
                      {
                        auth$.privileges.get()?.has('0.3.7.0') && !auth$.clientId.get() && (
                          <div className="grid">
                            <formControl.Field name="site.clientId">
                              {
                                field => (
                                  <>
                                    <div className="relative">
                                      <Label
                                        required
                                        className="text-xs"
                                        htmlFor="siteClientId"
                                      >
                                        Distributor
                                      </Label>

                                      <Computed>
                                        {
                                          () => (
                                            <Dropdown
                                              disabled
                                              label=""
                                              options={distributorList}
                                              value={field.state.value}
                                              onValueChange={
                                                (e) => {
                                                  if (e !== '') {
                                                    field.handleChange(e)
                                                    field.validate('change')
                                                  }
                                                }
                                              }
                                            />
                                          )
                                        }
                                      </Computed>
                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div>
                        )
                      }

                      <div className="grid">
                        <formControl.Field name="site.division.id">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="siteDivisionId"
                                  >
                                    Division
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          disabled
                                          label=""
                                          options={divisionList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="site.integrator.id">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="siteDivisionId"
                                  >
                                    Integrator
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          disabled
                                          label=""
                                          options={integratorList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <h4 className="text-lg font-semibold">
                      Product Feature Settings
                    </h4>

                    <formControl.Field
                      mode="array"
                      name="productFeatureSettings"
                    >
                      {
                        (field) => {
                          return (
                            <div className="w-full">
                              <div className="grid grid-cols-2 gap-2">
                                {
                                  field.state.value
                                  && field.state.value
                                    .filter(val => val.privileges === undefined)
                                    .map((_, j) => {
                                      return (
                                        <div className="grid gap-2">
                                          <formControl.Field
                                            key={j}
                                            name={`productFeatureSettings[${j}].featureValue`}
                                          >
                                            {
                                              (subSubField) => {
                                                return (
                                                  <>
                                                    <div className="relative w-full content-center">

                                                      {
                                                        formControl.getFieldValue(`productFeatureSettings[${j}].type`) === 'B'
                                                          ? (
                                                              <div className="align-center mt-4 flex content-center items-center">
                                                                <Label
                                                                  required
                                                                  className="text-xs"
                                                                  htmlFor={`productFeatureSettings[${j}].featureValue`}
                                                                >
                                                                  {
                                                                    formControl.getFieldValue(
                                                                      `productFeatureSettings[${j}].featureName`,
                                                                    )
                                                                  }
                                                                </Label>

                                                                <input
                                                                  checked={subSubField.state.value === '1'}
                                                                  className="ml-4 size-4" // Increased size for the checkbox
                                                                  id={`productFeatureSettings[${j}].featureValue`}
                                                                  type="checkbox"
                                                                  onBlur={subSubField.handleBlur}
                                                                  onChange={
                                                                    (e) => {
                                                                      subSubField.handleChange(e.target.checked ? '1' : '0')
                                                                      subSubField.validate('change')
                                                                    }
                                                                  }
                                                                />
                                                              </div>
                                                            )
                                                          : (
                                                              <>
                                                                <Label
                                                                  required
                                                                  className="text-xs"
                                                                  htmlFor="siteFeatureValue"
                                                                >
                                                                  {
                                                                    formControl.getFieldValue(
                                                                      `productFeatureSettings[${j}].featureName`,
                                                                    )
                                                                  }
                                                                </Label>

                                                                <Input
                                                                  className="mt-1"
                                                                  id={`productFeatureSettings[${j}].featureValue`}
                                                                  min={['MAX_LOGIN_USERS', 'MAX_LOGIN_SESSIONS_PER_USER'].includes(formControl.getFieldValue(`productFeatureSettings[${j}].featureCode`) ?? '') ? 1 : 0}
                                                                  type="number"
                                                                  value={subSubField.state.value === '0' ? '0' : subSubField.state.value.replace(/^0+/, '')}
                                                                  onBlur={subSubField.handleBlur}
                                                                  onChange={
                                                                    (e) => {
                                                                      subSubField.handleChange(e.target.value)
                                                                      subSubField.validate('change')
                                                                    }
                                                                  }
                                                                />
                                                              </>
                                                            )
                                                      }

                                                      {
                                                        subSubField.state.meta
                                                          .errors && (
                                                          <em
                                                            className="error-message"
                                                            role="alert"
                                                          >
                                                            {
                                                              subSubField.state.meta.errors.join(
                                                                ', ',
                                                              )
                                                            }
                                                          </em>
                                                        )
                                                      }
                                                    </div>
                                                  </>
                                                )
                                              }
                                            }
                                          </formControl.Field>
                                        </div>
                                      )
                                    })
                                }
                              </div>
                            </div>
                          )
                        }
                      }
                    </formControl.Field>
                  </>
                )
              }

              {
                activeStep === 1 && (
                  <>
                    <h4 className="mt-2 text-lg font-semibold">
                      Product Privilege Settings
                    </h4>

                    <div className="mt-4 grid grid-cols-1 gap-1">
                      <div className="grid gap-2">
                        <formControl.Field name="privilegeFeatures">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="siteServerType"
                                  >
                                    Privilege Features
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <MultiSelectDropdown
                                          options={productPrivilegeList}
                                          value={field.state.value as any}
                                          onChange={
                                            (e: any) => {
                                              field.handleChange(e)
                                              field.validate('change')
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }

                                <div className="mt-4">
                                  <Label className="text-xs">Selected Privileges</Label>

                                  <Computed>
                                    {
                                      () => {
                                        const renderPrivilegeItem = (privilege: any, level: number = 0) => {
                                          const handleToggleCollapse = (privilegeValue: string) => {
                                            setCollapsedIds((prevCollapsedIds) => {
                                              if (prevCollapsedIds.includes(privilegeValue)) {
                                                return prevCollapsedIds.filter(id => id !== privilegeValue)
                                              }
                                              else {
                                                return [...prevCollapsedIds, privilegeValue]
                                              }
                                            })
                                          }

                                          const updateChildren = (children: any[], isChecked: boolean): any[] => {
                                            return children.map(child => ({
                                              ...child,
                                              isChecked,
                                              children: updateChildren(child.children, isChecked), // Recursively update children
                                            }))
                                          }

                                          const updateParents = (privileges: any[], privilegeValue: string, isChecked: boolean): any[] => {
                                            const updatePrivilege = (privileges: any[], privilegeValue: string, isChecked: boolean): boolean => {
                                              let hasCheckedChildren = false

                                              for (const item of privileges) {
                                                if (item.value === privilegeValue) {
                                                  // Update the target privilege
                                                  item.isChecked = isChecked
                                                }

                                                if (item.children && item.children.length > 0) {
                                                  // Recursively update children
                                                  const childHasChecked = updatePrivilege(item.children, privilegeValue, isChecked)
                                                  hasCheckedChildren = hasCheckedChildren || childHasChecked
                                                }

                                                // After processing children, determine parent's `isChecked` based on children's state
                                                if (item.children && item.children.length > 0) {
                                                  const anyChildChecked = item.children.some((child: any) => child.isChecked)
                                                  item.isChecked = anyChildChecked || item.isChecked
                                                }
                                              }

                                              // Check if any item in this level is checked
                                              return privileges.some(priv => priv.isChecked)
                                            }

                                            const updateParentRecursively = (privileges: any[], privilegeValue: string): void => {
                                              for (const item of privileges) {
                                                if (item.children && item.children.some((child: any) => child.value === privilegeValue)) {
                                                  // Update parent based on the state of its children
                                                  const anyChildChecked = item.children.some((child: any) => child.isChecked)
                                                  item.isChecked = anyChildChecked

                                                  // Recursively move up to update the parent's parent
                                                  updateParentRecursively(privileges, item.value)
                                                }
                                                else if (item.children && item.children.length > 0) {
                                                  // Search for the target recursively in children
                                                  updateParentRecursively(item.children, privilegeValue)
                                                }
                                              }
                                            }

                                            // Step 1: Update the target privilege and propagate changes to its children
                                            updatePrivilege(privileges, privilegeValue, isChecked)

                                            // Step 2: Recursively update all parents except the root
                                            updateParentRecursively(privileges, privilegeValue)

                                            return privileges
                                          }

                                          const handleTogglePermission = (privilege: any, isChecked: boolean) => {
                                            const updatePrivileges = (privileges: any[]): any[] => {
                                              return privileges.map((item) => {
                                                if (item.value === privilege.value) {
                                                  // Update the current privilege and its children
                                                  return {
                                                    ...item,
                                                    isChecked,
                                                    children: updateChildren(item.children, isChecked),
                                                  }
                                                }

                                                if (item.children.length > 0) {
                                                  return {
                                                    ...item,
                                                    children: updatePrivileges(item.children), // Recursively update the tree
                                                  }
                                                }

                                                return item
                                              })
                                            }

                                            // Update the privileges tree
                                            let updatedPrivileges = updatePrivileges(field.state.value)

                                            // Update the parent privileges
                                            if (privilege.parent) {
                                              updatedPrivileges = updateParents(updatedPrivileges, privilege.parent, isChecked)
                                            }
                                            // Update the field state
                                            field.handleChange(updatedPrivileges)
                                          }

                                          return (
                                            <Fragment key={privilege?.value}>
                                              <tr className="items-center">
                                                <td
                                                  className="border-b border-r-4 border-slate-200 bg-white py-2 dark:border-gray-700 dark:bg-gray-800"
                                                  style={
                                                    {
                                                      paddingLeft: `${(level + 1) * 20}px`,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      position: 'sticky',
                                                      minWidth: '400px',
                                                      left: 0,
                                                    }
                                                  }
                                                  onClick={
                                                    (e) => {
                                                      e.preventDefault()
                                                      handleToggleCollapse(privilege?.value)
                                                    }
                                                  }
                                                >
                                                  {
                                                    privilege && privilege?.children?.length > 0 && (
                                                      <IconButton
                                                        className="size-4 bg-transparent"
                                                        color="muted"
                                                        style={{ marginRight: '8px' }}
                                                      >
                                                        <Icon icon={collapsedIds.includes(privilege?.id) ? 'lucide:plus' : 'lucide:minus'} />
                                                      </IconButton>
                                                    )
                                                  }

                                                  {privilege?.label}
                                                </td>

                                                <td
                                                  className="items-center border-b bg-white py-2 text-center dark:border-gray-700 dark:bg-gray-800"
                                                  style={{ width: '100px' }}
                                                >
                                                  <input
                                                    checked={privilege?.isChecked}
                                                    disabled={privilege?.isDisable}
                                                    style={{ transform: 'scale(1.5)' }}
                                                    type="checkbox"
                                                    onChange={e => handleTogglePermission(privilege, e.target.checked)}
                                                  />
                                                </td>
                                              </tr>

                                              {
                                                privilege?.children && !collapsedIds.includes(privilege?.id)
                                                && privilege?.children.map((child: any) => renderPrivilegeItem(child, level + 1))
                                              }
                                            </Fragment>
                                          )
                                        }

                                        return (
                                          <div style={{ width: '100%' }}>
                                            {
                                              Array.isArray(field.state.value) && field.state.value.map((privilege: any) => (
                                                <>
                                                  <caption style={{ captionSide: 'top', textAlign: 'left', fontWeight: 'bold', padding: '10px', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1, whiteSpace: 'nowrap' }}>
                                                    {privilege.featureName}
                                                  </caption>

                                                  <div
                                                    key={privilege.value}
                                                    style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid black', marginBottom: '10px' }}
                                                  >
                                                    <table
                                                      style={{ width: '100%' }}
                                                    >
                                                      <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                                        <tr>
                                                          <th style={{ border: '1px solid black', width: '50%' }}>Privilege</th>
                                                          <th style={{ border: '1px solid black', width: '30%' }}>Assign</th>
                                                        </tr>
                                                      </thead>

                                                      <tbody>
                                                        {renderPrivilegeItem(privilege)}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </>
                                              ))
                                            }
                                          </div>
                                        )
                                      }
                                    }
                                  </Computed>
                                </div>
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                    </div>
                  </>
                )
              }
            </CardContent>

            <CardFooter>
              <formControl.Subscribe
                selector={state => [state.canSubmit, state.isSubmitting]}
              >
                {
                  ([canSubmit, isSubmitting]) => (
                    <div className="flex w-full items-center justify-between">
                      {/* Placeholder for spacing when Back is hidden */}
                      <div>
                        {
                          activeStep !== 0 && (
                            <Button onClick={handleBack}>Back</Button>

                          )
                        }
                      </div>

                      <Button
                        color="primary"
                        disabled={!canSubmit && activeStep === steps.length - 1 && (!auth$.privileges.get()?.has('0.3.1.6'))}
                        variant="contained"
                        onClick={handleNext}
                      >
                        {activeStep === steps.length - 1 ? (isSubmitting ? '...' : 'Save') : 'Next'}
                      </Button>
                    </div>

                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
